/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/documents.md Marketing Constraints.
 *
 * AI document analysis is NOT production-trustworthy for every document type.
 * Describe as "AI can help" or "AI suggests," never as autonomous extraction.
 * The one solid AI story is cost reconciliation on receipt/invoice upload —
 * and even that always requires a human confirm.
 *
 * Document calibration is a blueprint concept, not a document concept — it
 * lives on the takeoffs page.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Upload,
  Link2,
  History,
  Shield,
  Search,
  Tags,
  FolderOpen,
  Building2,
  Briefcase,
  Receipt,
  Calculator,
  FileText,
  Eye,
  Layers,
  ClipboardList,
  DollarSign,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Documents",
  title: "Keep every job file",
  titleHighlight: "organized and connected.",
  description:
    "Store plans, contracts, permits, receipts, and photos in one place with clear links to the jobs, clients, and records they belong to.",

  featureGrid: {
    title: "A document system built for construction, not a file cabinet",
    items: [
      {
        icon: Upload,
        title: "Two Upload Paths",
        description:
          "Handle everyday files and large plan sets reliably so teams are not blocked by upload limits.",
      },
      {
        icon: Link2,
        title: "Link Documents Where They Belong",
        description:
          "Link one document to multiple related records so teams can find it wherever they are working.",
      },
      {
        icon: History,
        title: "Version History & Rollback",
        description:
          "Every update creates a new version. Roll back to any prior version in one click. Never lose the original.",
      },
      {
        icon: Tags,
        title: "Categories & Tags",
        description:
          "33 canonical document types (specs, RFI, permits, insurance, photos, contracts, and more) plus custom categories and tags per org. Filter and search by what matters to you.",
      },
      {
        icon: Search,
        title: "Search Across Everything",
        description:
          "Search across every document in your org, scoped by entity type, category, or tag. Results return the entities each document is attached to.",
      },
      {
        icon: Receipt,
        title: "Auto Cost Reconciliation",
        description:
          "Use receipt and invoice uploads to get cost-match suggestions, then confirm before posting anything.",
      },
      {
        icon: Shield,
        title: "Client Portal Access",
        description:
          "Share specs, photos, and final documents with your client through the portal. Read-only, no login friction — they see what you choose to show.",
      },
      {
        icon: Eye,
        title: "Split-Screen Viewer",
        description:
          "PDF viewer on the left, detail panel on the right. Navigate pages, zoom, and review document metadata and version history side by side.",
      },
      {
        icon: FolderOpen,
        title: "Reusable Document Tab",
        description:
          "The same documents tab renders on project, service, and client detail pages. Everything is one click away no matter where you start.",
      },
    ],
  },

  workflow: {
    title: "What a document actually does in CC",
    description:
      "A practical workflow from upload to linking, review, and sharing.",
    steps: [
      {
        label: "Upload",
        title: "Drop the file in",
        description:
          "Upload files directly from active work views so documentation stays close to the job.",
        details: [
          "Drag-and-drop from anywhere on a detail page",
          "Direct upload path for small files",
          "Large-file upload path for blueprints, drone imagery, and plan sets",
          "No 'file too large' errors on real construction documents",
        ],
      },
      {
        label: "Link",
        title: "Attach it where it belongs",
        description:
          "Connect each document to the related clients, projects, or estimates so teams can retrieve context quickly.",
        details: [
          "9 entity types supported",
          "One document, many links",
          "A primary home record so teams know where a document belongs first",
          "Entity-scoped queries return documents attached to each record",
        ],
      },
      {
        label: "Organize",
        title: "Tag, categorize, and search",
        description:
          "Use categories and tags to keep document libraries searchable as project volume grows.",
        details: [
          "33 canonical document types",
          "Custom org-level categories",
          "Tag system for cross-cutting classification",
          "Search by name, tag, category, or entity",
        ],
      },
      {
        label: "Review & Version",
        title: "Roll back when you need to",
        description:
          "Track revisions safely and roll back when needed so changes never erase historical context.",
        details: [
          "Automatic version numbering",
          "Rollback to any prior version in one click",
          "Archive older versions without cluttering active files",
          "Change description per version",
        ],
      },
      {
        label: "Share",
        title: "Portal access for clients",
        description:
          "Share selected documents in the client portal to improve transparency without exposing internal files.",
        details: [
          "Portal-visible flag per document",
          "Client portal renders selected documents",
          "Read-only by design",
          "Portal download activity is tracked for accountability",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Link2,
      title: "One Upload, Multiple Connections",
      description:
        "Keep document context connected across records without duplicating uploads.",
      bullets: [
        "9 entity types: client, jobsite, project, service, estimate, takeoff, proposal, supplier, organization",
        "Single source of truth per document, many links",
        "Set a primary home record for each document",
        "Change once, update everywhere",
        "Duplicate links are prevented automatically",
      ],
    },
    {
      icon: Upload,
      title: "Two Upload Paths — No Size Limits That Matter",
      description:
        "Support both quick uploads and larger construction files so field and office teams can use one system.",
      bullets: [
        "Direct upload for everyday files",
        "Large-file upload path for oversized construction documents",
        "Drag-and-drop on both paths",
        "Progress tracking while large files transfer",
        "Document and record links save together in one step",
      ],
    },
    {
      icon: History,
      title: "Version History With Rollback",
      description:
        "Preserve every revision so teams can trace what changed and restore earlier versions when needed.",
      bullets: [
        "Every update creates a new version automatically",
        "Change descriptions per version for context",
        "Rollback to any version from the version manager",
        "Archive older versions without cluttering active files",
        "Prior versions stay queryable and downloadable",
      ],
    },
    {
      icon: Receipt,
      title: "Auto Cost Reconciliation — Receipts Become Actuals",
      description:
        "Turn receipt and invoice uploads into suggested cost matches that teams can review before posting.",
      bullets: [
        "Auto-triggers on receipt / invoice upload — no button to press",
        "Side-by-side document preview and suggested costs",
        "Confirms required before anything becomes an actual",
        "Cost types stay aligned with matched estimate context",
        "Works for PDFs, images, spreadsheets, and Word documents",
      ],
    },
  ],

  connections: {
    title: "How documents connect to the rest of the platform",
    items: [
      {
        icon: Briefcase,
        flow: "Projects & Services → Documents",
        description:
          "Every project and service has a dedicated Documents tab powered by the same component. 33 canonical types plus custom categories. Auto-triggers cost reconciliation on receipt / invoice upload.",
      },
      {
        icon: Calculator,
        flow: "Estimates & Takeoffs → Plans",
        description:
          "Blueprint PDFs uploaded to a takeoff are documents too. The measurement tool reads them; the document library stores and versions them. One source of truth for the plan set.",
      },
      {
        icon: DollarSign,
        flow: "Receipts → Actual Costs",
        description:
          "A receipt or vendor invoice upload proposes matching line items. You confirm. The cost flows into the project's actual costs with the right cost type inherited from the matched estimate line.",
      },
      {
        icon: Building2,
        flow: "Documents → Client Portal",
        description:
          "Portal-visible documents render in the client portal next to proposals and invoices. Read-only, no login friction, you pick what they see.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Proposals",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Takeoffs & Blueprint Measurement",
      href: "/features/takeoffs",
      icon: Layers,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: ClipboardList,
    },
  ],
};

export function DocumentsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
